'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _formatErrorMessage from "@mui/x-internals/formatErrorMessage";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["occurrence", "ariaLabelledBy", "id", "variant", "className", "onClick"];
import * as React from 'react';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { useId } from '@base-ui/utils/useId';
import { useStore } from '@base-ui/utils/store';
import RepeatRounded from '@mui/icons-material/RepeatRounded';
import { schedulerEventSelectors, schedulerOtherSelectors, schedulerResourceSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { Button } from '@base-ui/react/button';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { useFormatTime } from "../../../hooks/useFormatTime.mjs";
import { useEventCalendarStyledContext } from "../../../../event-calendar/EventCalendarStyledContext.mjs";
import { getPaletteVariants } from "../../../utils/tokens.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const EventItemCard = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'EventItemCard'
})(({
  theme
}) => ({
  padding: 0,
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: (theme.vars || theme).palette.action.hover
  },
  '&:focus-visible': {
    outline: '2px solid var(--event-surface-accent)',
    outlineOffset: 1
  },
  '&[data-variant="compact"], &[data-variant="regular"]': {
    containerType: 'inline-size',
    cursor: 'pointer',
    height: 'fit-content'
  },
  '&[data-variant="filled"]': {
    backgroundColor: 'var(--event-surface-bold)',
    color: 'var(--event-on-surface-bold)',
    '&:hover': {
      backgroundColor: 'var(--event-surface-bold-hover)'
    },
    '&[data-editing]': {
      backgroundColor: 'var(--event-surface-selected)',
      color: 'var(--event-on-surface-selected)',
      '&:hover': {
        backgroundColor: 'var(--event-surface-selected-hover)'
      }
    }
  },
  '&[data-variant="regular"]': {
    cursor: 'pointer'
  },
  '&[data-editing]': {
    backgroundColor: 'var(--event-surface-selected)',
    '&:hover': {
      backgroundColor: 'var(--event-surface-selected-hover)'
    }
  },
  variants: getPaletteVariants(theme)
}));
const EventItemCardWrapper = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'EventItemCardWrapper'
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  '&[data-variant="compact"], &[data-variant="filled"]': {
    padding: `0 ${theme.spacing(0.5)}`
  }
}));
const EventItemTitle = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'EventItemTitle'
})(({
  theme
}) => ({
  margin: 0,
  color: (theme.vars || theme).palette.text.primary,
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: 'var(--EventCalendar-fontSize-eventTitle, 0.75rem)',
  lineHeight: 1.43,
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  }
}));
const EventItemTime = styled('time', {
  name: 'MuiEventCalendar',
  slot: 'EventItemTime'
})(({
  theme
}) => ({
  display: 'inline-block',
  color: (theme.vars || theme).palette.text.secondary,
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.43,
  whiteSpace: 'nowrap',
  width: 150,
  '&[data-compact]': {
    width: 'fit-content',
    marginInlineEnd: theme.spacing(0.5)
  },
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  }
}));
const EventItemRecurringIcon = styled(RepeatRounded, {
  name: 'MuiEventCalendar',
  slot: 'EventItemRecurringIcon'
})(({
  theme
}) => ({
  color: (theme.vars || theme).palette.text.primary,
  '[data-editing] &': {
    color: 'var(--event-on-surface-selected)'
  }
}));
const ResourceLegendColor = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'ResourceLegendColor'
})({
  width: 8,
  height: 8,
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: 'var(--event-main)',
  '[data-editing] &': {
    backgroundColor: 'var(--event-on-surface-selected)'
  }
});
const EventItemCardContent = styled('p', {
  name: 'MuiEventCalendar',
  slot: 'EventItemCardContent'
})({
  margin: 0,
  height: 20,
  lineHeight: '20px'
});
const EventItemLinesClamp = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'EventItemLinesClamp'
})({
  display: '-webkit-box',
  WebkitLineClamp: 'var(--number-of-lines)',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
  overflowWrap: 'break-word'
});

/**
 * Component used to display an event occurrence, without any positioning capabilities
 * Used in <AgendaView /> and in the event popover of <MonthView /> to display the list of events for a specific day.
 */
export const EventItem = /*#__PURE__*/React.forwardRef(function EventItem(props, forwardedRef) {
  const {
      occurrence,
      ariaLabelledBy,
      id: idProp,
      variant = 'regular',
      className,
      onClick
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);

  // Context hooks
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const store = useEventCalendarStoreContext();
  const isEditing = useStore(store, schedulerOtherSelectors.isEditedEvent, occurrence.id);

  // State hooks
  const id = useId(idProp);

  // Selector hooks
  const resource = useStore(store, schedulerResourceSelectors.processedResource, occurrence.resource);
  const color = useStore(store, schedulerEventSelectors.color, occurrence.id);
  const isRecurring = useStore(store, schedulerEventSelectors.isRecurring, occurrence.id);
  const formatTime = useFormatTime();
  const content = React.useMemo(() => {
    switch (variant) {
      case 'compact':
        return /*#__PURE__*/_jsxs(React.Fragment, {
          children: [/*#__PURE__*/_jsx(ResourceLegendColor, {
            className: classes.resourceLegendColor,
            role: "img",
            "aria-label": resource?.title ? localeText.resourceAriaLabel(resource.title) : localeText.noResourceAriaLabel
          }), /*#__PURE__*/_jsx(EventItemLinesClamp, {
            className: classes.eventItemLinesClamp,
            style: {
              '--number-of-lines': 1
            },
            children: /*#__PURE__*/_jsxs(EventItemCardContent, {
              className: classes.eventItemCardContent,
              children: [/*#__PURE__*/_jsx(EventItemTime, {
                className: classes.eventItemTime,
                "data-compact": true,
                children: /*#__PURE__*/_jsx("span", {
                  children: formatTime(occurrence.displayTimezone.start.value)
                })
              }), /*#__PURE__*/_jsx(EventItemTitle, {
                className: classes.eventItemTitle,
                children: occurrence.title
              })]
            })
          }), isRecurring && /*#__PURE__*/_jsx(EventItemRecurringIcon, {
            className: classes.eventItemRecurringIcon,
            "aria-hidden": "true",
            fontSize: "small"
          })]
        });
      case 'filled':
        return /*#__PURE__*/_jsxs(React.Fragment, {
          children: [/*#__PURE__*/_jsx(EventItemLinesClamp, {
            className: classes.eventItemLinesClamp,
            style: {
              '--number-of-lines': 1
            },
            children: /*#__PURE__*/_jsx(EventItemTitle, {
              className: classes.eventItemTitle,
              children: occurrence.title
            })
          }), isRecurring && /*#__PURE__*/_jsx(EventItemRecurringIcon, {
            className: classes.eventItemRecurringIcon,
            "aria-hidden": "true",
            fontSize: "small"
          })]
        });
      case 'regular':
        return /*#__PURE__*/_jsxs(React.Fragment, {
          children: [/*#__PURE__*/_jsx(ResourceLegendColor, {
            className: classes.resourceLegendColor,
            role: "img",
            "aria-label": resource?.title ? localeText.resourceAriaLabel(resource.title) : localeText.noResourceAriaLabel
          }), /*#__PURE__*/_jsx(EventItemLinesClamp, {
            className: classes.eventItemLinesClamp,
            style: {
              '--number-of-lines': 1
            },
            children: /*#__PURE__*/_jsxs(EventItemCardContent, {
              className: classes.eventItemCardContent,
              children: [/*#__PURE__*/_jsx(MultiDayDateLabel, {
                occurrence: occurrence,
                formatTime: formatTime
              }), /*#__PURE__*/_jsx(EventItemTitle, {
                className: classes.eventItemTitle,
                children: occurrence.title
              })]
            })
          }), isRecurring && /*#__PURE__*/_jsx(EventItemRecurringIcon, {
            className: classes.eventItemRecurringIcon,
            "aria-hidden": "true",
            fontSize: "small"
          })]
        });
      default:
        throw new Error(process.env.NODE_ENV !== "production" ? 'MUI X Scheduler: Unsupported variant provided to EventItem component. ' + 'The EventItem component only supports specific variant values. ' + 'Check the component documentation for supported variants.' : _formatErrorMessage(209));
    }
  }, [variant, resource?.title, localeText, formatTime, occurrence, isRecurring, classes]);
  return /*#__PURE__*/_jsx(Button, {
    nativeButton: false,
    onClick: onClick,
    render: /*#__PURE__*/_jsx(EventItemCard, _extends({
      ref: forwardedRef,
      id: id,
      "data-variant": variant,
      "data-palette": color,
      "data-editing": isEditing || undefined,
      "aria-labelledby": `${ariaLabelledBy} ${id}`
    }, other, {
      className: clsx(className, classes.eventItemCard, occurrence.className)
    })),
    children: /*#__PURE__*/_jsx(EventItemCardWrapper, {
      className: classes.eventItemCardWrapper,
      "data-variant": variant,
      children: content
    })
  });
});
if (process.env.NODE_ENV !== "production") EventItem.displayName = "EventItem";
function MultiDayDateLabel(props) {
  const {
    occurrence,
    formatTime
  } = props;
  const adapter = useAdapterContext();
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  if (!adapter.isSameDay(occurrence.displayTimezone.start.value, occurrence.displayTimezone.end.value)) {
    const format = `${adapter.formats.dayOfMonth} ${adapter.formats.month3Letters}`;
    return /*#__PURE__*/_jsx(EventItemTime, {
      className: classes.eventItemTime,
      as: "span",
      children: localeText.eventItemMultiDayLabel(adapter.formatByString(occurrence.displayTimezone.end.value, format))
    });
  }
  if (occurrence.allDay) {
    return /*#__PURE__*/_jsx(EventItemTime, {
      className: classes.eventItemTime,
      as: "span",
      children: localeText.allDay
    });
  }
  return /*#__PURE__*/_jsxs(EventItemTime, {
    className: classes.eventItemTime,
    children: [/*#__PURE__*/_jsx("span", {
      children: formatTime(occurrence.displayTimezone.start.value)
    }), /*#__PURE__*/_jsxs("span", {
      children: [" - ", formatTime(occurrence.displayTimezone.end.value)]
    })]
  });
}